import React, { useCallback, useMemo, useState } from "react";
import DataTable from "@/components/Datatable/Datatable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { useSubscriptionBillingData } from "../../hooks/useSubscriptionBillingData";
import { subscriptionBillingColumns } from "../SubscriptionBillingColumns/SubscriptionBillingColumns";
import SubscriptionBillingFilters from "../SubscriptionBillingFilters/SubscriptionBillingFilters";
import { CampaignStatusItem } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignStatus/types";
import { fetchCampaignStatus } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignStatus/fetchCampaignStatusApi";
import { useCampaignStatusMetadata } from "@/modules/administration/features/Stochastic/CampaignStatus/hooks/useCampaignStatusMetadata";
import { downloadFile } from "@/utils/downloadFile";

const CSV_HEADERS: string[] = [
  "Campaign Name",
  "Company Name",
  "Intacct ID",
  "Description",
  "Start Date",
  "End Date",
  "Status",
];

const toCsvRow = (item: CampaignStatusItem): string => {
  const escape = (val: string | undefined | null): string => {
    const str = val ?? "";
    return `"${str.replace(/"/g, '""')}"`;
  };
  return [
    escape(item.name),
    escape(item.company.name),
    escape(item.company.intacctId),
    escape(item.description),
    escape(item.startDate),
    escape(item.endDate),
    escape(item.campaignStatus?.name),
  ].join(",");
};

const SubscriptionBillingPage: React.FC = () => {
  const { metadata } = useCampaignStatusMetadata();
  const {
    campaigns,
    loading,
    error,
    totalCount,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  } = useSubscriptionBillingData();

  const [isExporting, setIsExporting] = useState(false);

  const columns = useMemo(() => subscriptionBillingColumns(), []);

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const response = await fetchCampaignStatus({
        page: 1,
        perPage: 10000,
        billingType: "subscription",
      });
      const rows = [CSV_HEADERS.join(","), ...response.data.map(toCsvRow)];
      const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      downloadFile(blob, "subscription-campaigns.csv");
    } catch (err) {
      console.error("Failed to export CSV:", err);
    } finally {
      setIsExporting(false);
    }
  }, []);

  return (
    <MainPageWrapper error={error} title="Campaign Invoices - Subscription Billing">
      <SubscriptionBillingFilters
        campaignStatuses={metadata?.campaignStatusesData ?? []}
        filters={filters}
        isExporting={isExporting}
        onExportCsv={handleExportCsv}
        onFilterChange={handleFilterChange}
      />

      <DataTable<CampaignStatusItem>
        columns={columns}
        data={campaigns}
        error={error}
        loading={loading}
        noDataMessage="No subscription campaigns found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(row) => row.id.toString()}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default SubscriptionBillingPage;
